import { Link, useLoaderData, useSearch } from "@tanstack/react-router";
import { ArrowLeft, BarChart3, ChevronsUpDown, Loader2, Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CurrencyInput } from "@/components/shared/currency-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import { GoalsAndTargets } from "./goals/goals-and-targets";
import { PercentInput, savedFieldClass } from "./goals/shared";

interface Owner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  hubspotOwnerId: string | null;
  managerId: string | null;
  managerName: string | null;
  baseSalary: number | null;
  annualBonusPercent: number | null;
  equityPercent: number | null;
  commissionQuarterlyBaseline: number | null;
  commissionPerDeal: number | null;
  commissionErrorRateThreshold: number | null;
  campusCommissionYear2: number | null;
  campusCommissionYear3: number | null;
  commissionType: string | null;
  nonRelationalGpmThreshold: number | null;
  relationalGpmThreshold: number | null;
  standardMarginGpmThreshold: number | null;
  executiveView: boolean;
}

export function OwnerDetailPage({ readOnly = false }: { readOnly?: boolean }) {
  const { owner: initialOwner } = useLoaderData({
    from: "/_authenticated/owner-detail/$ownerId",
  }) as { owner: Owner };

  const { from: referrer, tab: initialTab } = useSearch({
    from: "/_authenticated/owner-detail/$ownerId" as any,
  }) as { from?: string; tab?: string };

  const backTo = referrer === "org-chart" ? "/org-chart" : "/deal-metrics";
  const backLabel = referrer === "org-chart" ? "Org Chart" : "Owners";

  const [form, setForm] = useState({
    email: initialOwner.email ?? "",
    role: initialOwner.role ?? "",
    baseSalary: initialOwner.baseSalary ?? "",
    annualBonusPercent: initialOwner.annualBonusPercent ?? "",
    equityPercent: initialOwner.equityPercent ?? "",
    commissionQuarterlyBaseline: initialOwner.commissionQuarterlyBaseline ?? "",
    commissionPerDeal: initialOwner.commissionPerDeal ?? "",
    commissionErrorRateThreshold: initialOwner.commissionErrorRateThreshold ?? "",
    commissionType: initialOwner.commissionType ?? "standard",
    campusCommissionYear2: initialOwner.campusCommissionYear2 ?? "",
    campusCommissionYear3: initialOwner.campusCommissionYear3 ?? "",
    nonRelationalGpmThreshold: initialOwner.nonRelationalGpmThreshold ?? "",
    relationalGpmThreshold: initialOwner.relationalGpmThreshold ?? "",
    standardMarginGpmThreshold: initialOwner.standardMarginGpmThreshold ?? "",
  });

  const [executiveView, setExecutiveView] = useState<boolean>(initialOwner.executiveView);
  const [savedField, setSavedField] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastSavedRef = useRef({ ...form });

  const saveExecutiveView = useCallback(
    async (value: boolean) => {
      const res = await fetch(`/api/owners/${initialOwner.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ executiveView: value }),
      });
      if (res.ok) {
        clearTimeout(savedTimerRef.current);
        setSavedField("executiveView");
        savedTimerRef.current = setTimeout(() => setSavedField(null), 800);
      } else {
        setExecutiveView(!value);
        const errBody = await res.json().catch(() => ({ error: "Save failed" }));
        toast.error(errBody.error ?? "Save failed");
      }
    },
    [initialOwner.id],
  );

  function updateField(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const saveForm = useCallback(
    async (field: string, currentForm: typeof form) => {
      if (
        String(currentForm[field as keyof typeof currentForm]) ===
        String(lastSavedRef.current[field as keyof typeof currentForm])
      ) {
        return;
      }

      const body: Record<string, unknown> = {
        email: currentForm.email,
        role: currentForm.role || null,
        baseSalary: currentForm.baseSalary === "" ? null : Number(currentForm.baseSalary),
        annualBonusPercent:
          currentForm.annualBonusPercent === "" ? null : Number(currentForm.annualBonusPercent),
        equityPercent: currentForm.equityPercent === "" ? null : Number(currentForm.equityPercent),
        commissionQuarterlyBaseline:
          currentForm.commissionQuarterlyBaseline === ""
            ? null
            : Number(currentForm.commissionQuarterlyBaseline),
        commissionPerDeal:
          currentForm.commissionPerDeal === "" ? null : Number(currentForm.commissionPerDeal),
        commissionErrorRateThreshold:
          currentForm.commissionErrorRateThreshold === ""
            ? null
            : Number(currentForm.commissionErrorRateThreshold),
        commissionType: currentForm.commissionType || "standard",
        campusCommissionYear2:
          currentForm.campusCommissionYear2 === ""
            ? null
            : Number(currentForm.campusCommissionYear2),
        campusCommissionYear3:
          currentForm.campusCommissionYear3 === ""
            ? null
            : Number(currentForm.campusCommissionYear3),
        nonRelationalGpmThreshold:
          currentForm.nonRelationalGpmThreshold === ""
            ? null
            : Number(currentForm.nonRelationalGpmThreshold),
        relationalGpmThreshold:
          currentForm.relationalGpmThreshold === ""
            ? null
            : Number(currentForm.relationalGpmThreshold),
        standardMarginGpmThreshold:
          currentForm.standardMarginGpmThreshold === ""
            ? null
            : Number(currentForm.standardMarginGpmThreshold),
      };

      const res = await fetch(`/api/owners/${initialOwner.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        lastSavedRef.current = { ...currentForm };
        clearTimeout(savedTimerRef.current);
        setSavedField(field);
        savedTimerRef.current = setTimeout(() => setSavedField(null), 800);
      } else {
        const prev = lastSavedRef.current[field as keyof typeof currentForm];
        setForm((f) => ({ ...f, [field]: prev }));
        const errBody = await res.json().catch(() => ({ error: "Save failed" }));
        toast.error(errBody.error ?? "Save failed");
      }
    },
    [initialOwner.id],
  );

  function handleBlur(field: string) {
    if (readOnly) return;
    saveForm(field, form);
  }

  const fieldClass = (field: string) => savedFieldClass(savedField, field);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backTo}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {backLabel}
          </Link>
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {initialOwner.firstName} {initialOwner.lastName}
          </h1>
          {initialOwner.hubspotOwnerId && (
            <p className="text-xs font-mono text-muted-foreground mt-1">
              HubSpot ID: {initialOwner.hubspotOwnerId}
            </p>
          )}
        </div>
        {initialOwner.hubspotOwnerId && (
          <Button variant="outline" size="sm" asChild>
            <Link
              to="/owner-metrics/$ownerId"
              params={{ ownerId: initialOwner.hubspotOwnerId }}
              search={{ from: "owner-detail", ownerDetailId: initialOwner.id }}
            >
              <BarChart3 className="mr-1.5 h-4 w-4" />
              View Dashboard
            </Link>
          </Button>
        )}
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                className={fieldClass("email")}
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2">
              <Label>Reports To</Label>
              <p className="text-sm text-muted-foreground pt-1">
                {initialOwner.managerName ?? "Not assigned"}
                <span className="text-xs ml-2">(managed via Org Chart)</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input
                id="role"
                value={form.role}
                onChange={(e) => updateField("role", e.target.value)}
                onBlur={() => handleBlur("role")}
                className={fieldClass("role")}
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="baseSalary">Base Salary</Label>
              <CurrencyInput
                id="baseSalary"
                value={form.baseSalary}
                onChange={(v) => updateField("baseSalary", v)}
                onBlur={() => handleBlur("baseSalary")}
                placeholder="e.g. $46,000"
                className={fieldClass("baseSalary")}
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="annualBonusPercent">Annual Bonus (% of base)</Label>
              <PercentInput
                id="annualBonusPercent"
                value={form.annualBonusPercent}
                onChange={(v) => updateField("annualBonusPercent", v)}
                onBlur={() => handleBlur("annualBonusPercent")}
                placeholder="e.g. 10%"
                className={fieldClass("annualBonusPercent")}
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="equityPercent">Equity (%)</Label>
              <PercentInput
                id="equityPercent"
                value={form.equityPercent}
                onChange={(v) => updateField("equityPercent", v)}
                onBlur={() => handleBlur("equityPercent")}
                placeholder="e.g. 2%"
                className={fieldClass("equityPercent")}
                disabled={readOnly}
              />
            </div>
            <div
              className={cn(
                "sm:col-span-2 flex items-start justify-between gap-4 rounded-md border px-4 py-3",
                fieldClass("executiveView"),
              )}
            >
              <div>
                <Label htmlFor="executiveView" className="text-sm">
                  Executive Dashboard
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Adds a company-wide monthly performance tracker (Opportunities, Closed Deals,
                  Close Rate, Gross Sales, GPM) to this owner's dashboard.
                </p>
              </div>
              <Switch
                id="executiveView"
                checked={executiveView}
                onCheckedChange={(v) => {
                  if (readOnly) return;
                  setExecutiveView(v);
                  saveExecutiveView(v);
                }}
                disabled={readOnly}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Commission Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="commissionType">Commission Type</Label>
              <Select
                value={form.commissionType}
                onValueChange={(v) => {
                  if (readOnly) return;
                  updateField("commissionType", v);
                  saveForm("commissionType", { ...form, commissionType: v });
                }}
                disabled={readOnly}
              >
                <SelectTrigger className={fieldClass("commissionType")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (Per-Deal)</SelectItem>
                  <SelectItem value="non_relational">Non-Relational (GPM-Based)</SelectItem>
                  <SelectItem value="standard_margin">Standard Margin-Based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="commissionQuarterlyBaseline">Quarterly Baseline</Label>
                <Input
                  id="commissionQuarterlyBaseline"
                  type="number"
                  value={form.commissionQuarterlyBaseline}
                  onChange={(e) => updateField("commissionQuarterlyBaseline", e.target.value)}
                  onBlur={() => handleBlur("commissionQuarterlyBaseline")}
                  className={fieldClass("commissionQuarterlyBaseline")}
                  placeholder="e.g. 300"
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">Deals before commission kicks in</p>
              </div>
              {form.commissionType === "standard" && (
                <div className="space-y-2">
                  <Label htmlFor="commissionPerDeal">Per-Deal Commission</Label>
                  <CurrencyInput
                    id="commissionPerDeal"
                    value={form.commissionPerDeal}
                    onChange={(v) => updateField("commissionPerDeal", v)}
                    onBlur={() => handleBlur("commissionPerDeal")}
                    placeholder="e.g. $35"
                    className={fieldClass("commissionPerDeal")}
                    disabled={readOnly}
                  />
                  <p className="text-xs text-muted-foreground">
                    Amount paid per deal above baseline
                  </p>
                </div>
              )}
              {form.commissionType === "standard_margin" && (
                <div className="space-y-2">
                  <Label htmlFor="standardMarginGpmThreshold">Gross Profit Margin</Label>
                  <PercentInput
                    id="standardMarginGpmThreshold"
                    value={form.standardMarginGpmThreshold}
                    onChange={(v) => updateField("standardMarginGpmThreshold", v)}
                    onBlur={() => handleBlur("standardMarginGpmThreshold")}
                    placeholder="e.g. 10%"
                    className={fieldClass("standardMarginGpmThreshold")}
                    disabled={readOnly}
                  />
                  <p className="text-xs text-muted-foreground">
                    GPM % multiplier for commission payout
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="commissionErrorRateThreshold">Error Rate Threshold</Label>
                <PercentInput
                  id="commissionErrorRateThreshold"
                  value={form.commissionErrorRateThreshold}
                  onChange={(v) => updateField("commissionErrorRateThreshold", v)}
                  onBlur={() => handleBlur("commissionErrorRateThreshold")}
                  placeholder="e.g. 1%"
                  className={fieldClass("commissionErrorRateThreshold")}
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">
                  Max error rate before commission is affected
                </p>
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="campusCommissionYear2">Campus Commission Year 2</Label>
                <CurrencyInput
                  id="campusCommissionYear2"
                  value={form.campusCommissionYear2}
                  onChange={(v) => updateField("campusCommissionYear2", v)}
                  onBlur={() => handleBlur("campusCommissionYear2")}
                  placeholder="e.g. $500"
                  className={fieldClass("campusCommissionYear2")}
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">Year 2 campus affiliate commission</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="campusCommissionYear3">Campus Commission Year 3</Label>
                <CurrencyInput
                  id="campusCommissionYear3"
                  value={form.campusCommissionYear3}
                  onChange={(v) => updateField("campusCommissionYear3", v)}
                  onBlur={() => handleBlur("campusCommissionYear3")}
                  placeholder="e.g. $750"
                  className={fieldClass("campusCommissionYear3")}
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">Year 3 campus affiliate commission</p>
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="nonRelationalGpmThreshold">Non-Relational GPM Threshold</Label>
                <PercentInput
                  id="nonRelationalGpmThreshold"
                  value={form.nonRelationalGpmThreshold}
                  onChange={(v) => updateField("nonRelationalGpmThreshold", v)}
                  onBlur={() => handleBlur("nonRelationalGpmThreshold")}
                  placeholder="e.g. 50%"
                  className={fieldClass("nonRelationalGpmThreshold")}
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">Min GPM % before commission applies</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="relationalGpmThreshold">Relational GPM Threshold</Label>
                <PercentInput
                  id="relationalGpmThreshold"
                  value={form.relationalGpmThreshold}
                  onChange={(v) => updateField("relationalGpmThreshold", v)}
                  onBlur={() => handleBlur("relationalGpmThreshold")}
                  placeholder="e.g. 50%"
                  className={fieldClass("relationalGpmThreshold")}
                  disabled={readOnly}
                />
                <p className="text-xs text-muted-foreground">Min GPM % before commission applies</p>
              </div>
            </div>
            <Separator />
            <div className="rounded-md bg-muted/30 px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">
                {form.commissionType === "non_relational"
                  ? "Non-Relational Formula"
                  : form.commissionType === "standard_margin"
                    ? "Standard Margin-Based Formula"
                    : "Standard Formula"}
              </p>
              <p className="text-sm font-mono">
                {form.commissionType === "non_relational"
                  ? "Payout = Avg Gross Profit × Eligible × NR GPM Threshold"
                  : form.commissionType === "standard_margin"
                    ? "Payout = Avg Gross Profit × Eligible × GPM Threshold"
                    : "Payout = Eligible × Per-Deal Rate"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Eligible = Total Orders − Errors − Baseline
                {form.commissionType === "non_relational" &&
                  " · Avg Gross Profit = Total Net Sales ÷ Total Orders · NR deals only"}
                {form.commissionType === "standard_margin" &&
                  " · Avg Gross Profit = Total Net Sales ÷ Total Orders · All deals"}
              </p>
            </div>
          </CardContent>
        </Card>

        <CommissionEligibilityCard ownerId={initialOwner.id} readOnly={readOnly} />

        <DashboardAccessCard ownerId={initialOwner.id} readOnly={readOnly} />

        <QuickLinksCard
          ownerId={initialOwner.id}
          ownerName={initialOwner.firstName}
          readOnly={readOnly}
        />
      </div>

      <GoalsAndTargets
        ownerId={initialOwner.id}
        ownerName={initialOwner.firstName}
        initialSection={initialTab}
        readOnly={readOnly}
      />
    </div>
  );
}

const QUARTER_OPTIONS = [
  { key: "Q1", range: "Apr–Jun" },
  { key: "Q2", range: "Jul–Sep" },
  { key: "Q3", range: "Oct–Dec" },
  { key: "Q4", range: "Jan–Mar" },
] as const;

/**
 * Lets an admin turn a specific fiscal-year quarter off for this owner — e.g. someone who
 * just started shouldn't earn a bonus until the following quarter. Commission math always
 * computes all four quarters uniformly from one config, so this is a per-quarter override on
 * top of that, not a formula change: deal counts and GPM still show, just no payout.
 */
function CommissionEligibilityCard({ ownerId, readOnly }: { ownerId: string; readOnly: boolean }) {
  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear());
  const [excluded, setExcluded] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setExcluded(null);
    try {
      const res = await fetch(`/api/owners/${ownerId}/commission-exclusions?fy=${fiscalYear}`);
      const data = res.ok ? await res.json() : { exclusions: [] };
      setExcluded(data.exclusions ?? []);
    } catch {
      setExcluded([]);
    }
  }, [ownerId, fiscalYear]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (quarter: string, eligible: boolean) => {
    const next = eligible
      ? (excluded ?? []).filter((q) => q !== quarter)
      : [...(excluded ?? []), quarter];
    setExcluded(next);
    setSaving(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/commission-exclusions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear, exclusions: next }),
      });
      if (!res.ok) throw new Error();
      toast.success("Bonus eligibility updated");
    } catch {
      toast.error("Failed to update bonus eligibility");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const availableFYs = [currentFiscalYear() - 1, currentFiscalYear(), currentFiscalYear() + 1];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Bonus Eligibility</CardTitle>
          <Select value={String(fiscalYear)} onValueChange={(v) => setFiscalYear(Number(v))}>
            <SelectTrigger className="h-8 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableFYs.map((fy) => (
                <SelectItem key={fy} value={String(fy)}>
                  {formatFY(fy)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Turn a quarter off to skip commission for it entirely — deal counts still show on the
          dashboard, but no bonus is calculated for that quarter.
        </p>
        {excluded === null ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {QUARTER_OPTIONS.map((q) => (
              <div
                key={q.key}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{q.key}</span>
                  <span className="text-[10px] text-muted-foreground">{q.range}</span>
                </div>
                <Switch
                  checked={!excluded.includes(q.key)}
                  disabled={readOnly || saving}
                  onCheckedChange={(on) => toggle(q.key, on)}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const DASHBOARD_OPTIONS = [
  { key: "manufacturing", label: "Manufacturing KPIs" },
  { key: "operations", label: "Operations KPIs" },
  { key: "marketing", label: "Marketing Suite" },
  { key: "consultant", label: "Consultant Hub" },
] as const;

/**
 * Assigns ops dashboards to this owner. Access follows the org tree — the owner
 * and anyone above them in the managerId chain can view assigned dashboards.
 */
function DashboardAccessCard({ ownerId, readOnly }: { ownerId: string; readOnly: boolean }) {
  const [assigned, setAssigned] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/owners/${ownerId}/dashboard-assignments`);
      const data = res.ok ? await res.json() : { dashboards: [] };
      setAssigned(data.dashboards ?? []);
    } catch {
      setAssigned([]);
    }
  }, [ownerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (key: string, on: boolean) => {
    const next = on ? [...(assigned ?? []), key] : (assigned ?? []).filter((d) => d !== key);
    setAssigned(next);
    setSaving(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/dashboard-assignments`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dashboards: next }),
      });
      if (!res.ok) throw new Error();
      toast.success("Dashboard access updated");
    } catch {
      toast.error("Failed to update dashboard access");
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Dashboard Access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Ops dashboards this person is responsible for. Anyone they report to (up the org chart)
          can view these too. Admins always have access.
        </p>
        {assigned === null ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : (
          DASHBOARD_OPTIONS.map((opt) => (
            <div key={opt.key} className="flex items-center justify-between">
              <span className="text-sm">{opt.label}</span>
              <Switch
                checked={assigned.includes(opt.key)}
                disabled={readOnly || saving}
                onCheckedChange={(on) => toggle(opt.key, on)}
              />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

interface QuickLinkPerson {
  id: string;
  name: string;
  hubspotOwnerId: string | null;
}

/**
 * Pins extra people to this owner's dashboard as quick-jump shortcuts. These are
 * a convenience only — they do NOT change reporting lines or grant any access
 * (the person still needs to be viewable via the org chart to open the page).
 */
function QuickLinksCard({
  ownerId,
  ownerName,
  readOnly,
}: {
  ownerId: string;
  ownerName: string;
  readOnly: boolean;
}) {
  const [links, setLinks] = useState<QuickLinkPerson[] | null>(null);
  const [allOwners, setAllOwners] = useState<QuickLinkPerson[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [linkRes, ownersRes] = await Promise.all([
        fetch(`/api/owners/${ownerId}/quick-links`),
        fetch("/api/owners?pageSize=1000&sortBy=firstName&sortDir=asc"),
      ]);
      const linkData = linkRes.ok ? await linkRes.json() : { quickLinks: [] };
      const ownersData = ownersRes.ok ? await ownersRes.json() : { owners: [] };
      setLinks(linkData.quickLinks ?? []);
      setAllOwners(
        // biome-ignore lint/suspicious/noExplicitAny: raw owner rows from /api/owners
        (ownersData.owners ?? []).map((o: any) => ({
          id: o.id,
          name: `${o.firstName} ${o.lastName}`,
          hubspotOwnerId: o.hubspotOwnerId ?? null,
        })),
      );
    } catch {
      setLinks([]);
    }
  }, [ownerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (next: QuickLinkPerson[]) => {
    setSaving(true);
    setLinks(next);
    try {
      const res = await fetch(`/api/owners/${ownerId}/quick-links`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ linkedOwnerIds: next.map((p) => p.id) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLinks(data.quickLinks ?? next);
      toast.success("Quick links updated");
    } catch {
      toast.error("Failed to update quick links");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const add = (person: QuickLinkPerson) => {
    setPickerOpen(false);
    if (links?.some((p) => p.id === person.id)) return;
    void save([...(links ?? []), person]);
  };

  const remove = (id: string) => {
    void save((links ?? []).filter((p) => p.id !== id));
  };

  const selectableOwners = allOwners.filter(
    (o) => o.id !== ownerId && !links?.some((p) => p.id === o.id),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Quick Links</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Pin people to {ownerName}'s dashboard for one-click access — handy for helping lead a team
          that isn't a direct report. This is a shortcut only; it doesn't change reporting lines.
        </p>
        {links === null ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {links.length === 0 ? (
                <span className="text-sm text-muted-foreground/60">None pinned yet</span>
              ) : (
                links.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-sm"
                  >
                    {p.name}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => remove(p.id)}
                        disabled={saving}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${p.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
            {!readOnly && (
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" disabled={saving} className="h-8">
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add person
                    <ChevronsUpDown className="ml-1 h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search people…" />
                    <CommandList>
                      <CommandEmpty>No people found.</CommandEmpty>
                      {selectableOwners.map((o) => (
                        <CommandItem key={o.id} value={o.name} onSelect={() => add(o)}>
                          {o.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
